<template>
  <div class="day-trade">
    <div class="day-trade__main">
      <section class="day-trade__header">
        <Heading class="day-trade__header-title" :level="2">
          Limite alocado
        </Heading>
        <Heading :level="2">
          <Money>{{ marginAllocation.balance.formatted }}</Money>
        </Heading>
        <div class="day-trade__header-rows">
          <Row
            v-for="(item, index) in details"
            :key="index"
            :left-title="item.title"
            :has-line="false"
            chevron
            has-interaction
            :data-testid="`details-modal-${index}`"
            @click="handleModalOpen(item)"
          >
            <template #top-right>
              <Money>{{ item.formatted }}</Money>
            </template>
          </Row>
        </div>
      </section>
      <div class="day-trade__button-container">
        <button class="day-trade__button-box" @click="openTraderShield">
          <div>
            <BaseText size="p4" :color="NebraskaColors.elementPrimary">
              Trader Shield
              <ChipStatus
                class="day-trade__button-box--chip"
                :label="traderShieldChip.label"
                :status="traderShieldChip.status"
              />
            </BaseText>
            <BaseText size="p5">{{ traderShieldSubtitle }}</BaseText>
          </div>
          <BaseIcon icon="EA0170" />
        </button>
        <div class="day-trade__button-container--divider" />
        <div
          class="day-trade__toggle"
          role="button"
          tabindex="0"
          @click.prevent="handleInputPreference"
        >
          <ToggleSwitch
            :value="dataForm.isPreferencesSaved"
            hide-divider
            :disabled="dataForm.isUpdatingPreferences"
          >
            <BaseText size="p4" :color="NebraskaColors.elementPrimary">
              Limite recorrente
            </BaseText>
            <template #subtitle>
              <BaseText size="p5">
                Utilizar mesmo limite nos dias seguintes
              </BaseText>
            </template>
          </ToggleSwitch>
        </div>
      </div>
    </div>

    <DetailsModal
      :is-open="isDetailsModalOpen"
      :data="modalDetails"
      @close="isDetailsModalOpen = false"
    />

    <TraderShield
      :is-open="isTraderShieldOpen"
      :edit-mode="isEditMode"
      @close="isTraderShieldOpen = false"
    />

    <DeallocateLimit
      :is-open="isDeallocateOpen"
      @close="isDeallocateOpen = false"
    />

    <VariationModal
      :is-open="isVariationModalOpen"
      @continue="changeStep(EMarginAllocationSteps.BALANCE)"
      @close="isVariationModalOpen = false"
    />

    <RecurringLimit
      :is-open="isRecurringLimitOpen"
      @close="isRecurringLimitOpen = false"
    />

    <ButtonContainer class="day-trade__footer">
      <template #button-primary>
        <ButtonPrimary
          size="large"
          :label="allocateButton.label"
          :disabled="allocateButton.disabled"
          data-testid="allocate-button"
          @click="handleClick(allocateButton.type, 'editar')"
        />
      </template>
      <template v-if="deallocateButton" #button-secondary>
        <ButtonSecondary
          size="large"
          :label="deallocateButton.label"
          :disabled="deallocateButton.disabled"
          @click="handleClick(deallocateButton.type, 'desalocar')"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, Ref, ref } from 'vue';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  Heading,
  BaseText,
  Row,
  ChipStatus,
  BaseIcon,
  ToggleSwitch,
  ButtonContainer,
  ButtonPrimary,
  ButtonSecondary
} from '@warrenbrasil/nebraska-web';
import { Money } from '@/modules/common/components/Money';
import {
  DetailsModal,
  TraderShield,
  DeallocateLimit,
  VariationModal,
  RecurringLimit
} from './components';

import { EMarginAllocationSteps } from '@/modules/trade/views/MarginAllocationV2/helpers';

import {
  IMarginAllocationV2,
  IAmountDetails
} from '@/modules/trade/services/margin-allocation';

enum EButtonTypes {
  ALLOCATE = 'allocate',
  DEALLOCATE = 'deallocate',
  DISABLE_TRADER_SHIELD = 'disableTraderShield',
  HAS_POSITION = 'hasPosition',
  OPEN_VARIATION = 'openVariation'
}

const updatePreference = inject('fetchUpdatePreference', () => ({}));
const changeStep = inject('changeStep', (step: EMarginAllocationSteps) => ({
  step
}));

const dataForm = inject('dataForm') as Ref<{
  isUpdatingPreferences: boolean;
  isUpdatingTraderShield: boolean;
  isPreferencesSaved: boolean;
}>;
const marginAllocation = inject('marginAllocation') as Ref<IMarginAllocationV2>;

const modalDetails = ref<IAmountDetails>();
const isDetailsModalOpen = ref(false);
const isTraderShieldOpen = ref(false);
const isEditMode = ref(false);
const isDeallocateOpen = ref(false);
const isVariationModalOpen = ref(false);
const isRecurringLimitOpen = ref(false);

const allocateButton = computed(() => {
  return marginAllocation.value.allocateButton;
});

const deallocateButton = computed(() => {
  return marginAllocation.value.deallocateButton;
});

const isTraderShieldEnabled = computed(
  () => marginAllocation.value.isTraderShieldEnabled
);

const details = computed(() => {
  return [
    marginAllocation.value.availableBalance,
    marginAllocation.value.dailyLoss
  ];
});

const traderShieldSubtitle = computed(() => {
  return isTraderShieldEnabled.value
    ? 'Proteção extra para suas operações'
    : 'Ative proteção extra para suas operações';
});

const traderShieldChip = computed(() => {
  return isTraderShieldEnabled.value
    ? {
        label: 'Ativo',
        status: 'positive'
      }
    : {
        label: 'Inativo',
        status: 'alert'
      };
});

function openTraderShield() {
  isEditMode.value = false;
  isTraderShieldOpen.value = true;
}

function handleClick(type: string, text: string) {
  switch (type as EButtonTypes) {
    case EButtonTypes.ALLOCATE:
      changeStep(EMarginAllocationSteps.BALANCE);
      break;

    case EButtonTypes.DEALLOCATE:
      isDeallocateOpen.value = true;
      break;

    case EButtonTypes.HAS_POSITION:
      notify({
        group: 'wrn-notifications',
        text: `Não é possível ${text} o limite, pois existem posições em aberto.`,
        type: NotificationTypes.error
      });
      break;

    case EButtonTypes.DISABLE_TRADER_SHIELD:
      isEditMode.value = true;
      isTraderShieldOpen.value = true;
      break;

    case EButtonTypes.OPEN_VARIATION:
      isVariationModalOpen.value = true;
      break;

    default:
      break;
  }
}

function handleModalOpen(details: IAmountDetails) {
  modalDetails.value = details;
  isDetailsModalOpen.value = true;
}

function handleInputPreference() {
  if (marginAllocation.value.isPreferencesSaved) return updatePreference();

  isRecurringLimitOpen.value = true;
}
</script>

<style lang="less" scoped>
@navbar-height: 64px;
@header-height: 416px;

.day-trade {
  &__main {
    max-width: 562px;
    margin: 0 auto;
    padding: @size-spacing-x1000 @size-spacing-x400 @size-spacing-x1800;
  }

  &__header {
    margin-bottom: @size-spacing-x800;

    &-title {
      color: @element-secondary;
    }

    &-rows {
      margin-top: @size-spacing-x800;

      .row-item {
        background-color: transparent;

        :deep(.container-left) {
          color: @element-secondary;
        }
      }

      :deep(.row-item > div) {
        padding: @size-spacing-x300 0;
      }
    }
  }

  &__button-container {
    background-color: @background-secondary;
    border: @size-border-x400 solid @divider-primary;
    border-radius: @size-radius-x300;
    padding: @size-spacing-x500;

    &--divider {
      width: 100%;
      height: 1px;
      background-color: @divider-primary;
      margin: @size-spacing-x400 0;
    }
  }

  &__button-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0;
    text-align: left;

    &--chip {
      margin-left: @size-spacing-x300;
    }
  }

  &__toggle {
    :deep(.switch-wrapper) {
      height: initial;
    }
  }

  &__footer {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
    text-align: center;
  }
}
</style>
